import equals from 'ramda/es/equals';
import { ViewType, StudyCriteriaCategory, SeriesCriteriaCategory } from 'generated/graphql';
import type { HangingProtocolPayload } from 'generated/graphql';
import type {
  StepConfiguration,
  ViewportConfigurationEntry,
} from '../../../../HangingProtocol/steppedHPTypes';
import {
  stepToGqlInput,
  isEmptyViewport,
  parseLayoutString,
} from '../../../../HangingProtocol/steppedHPTypes';
import { stepViewportToFormViewport } from '../../../../HangingProtocol/hooks/useStepFormSync';
import { generateViewportId, viewportIdToConfig } from '../../../viewerUtils';
import { logger } from 'modules/logger';
import type { Layouts } from '../../../StudyLoader/viewerLoaderState';
import type { SubmitFormIntent } from '../constants';
import { SUBMIT_FORM_INTENT } from '../constants';

// Extended type that includes both ontology_id and display_name for UI display
export type ExtendedStudyCriteria = {
  category: StudyCriteriaCategory;
  value: string; // ontology_id
  displayName: string; // display_name for UI
};

// Extended type for series criteria that includes both ontology_id and display_name for UI display
export type ExtendedSeriesCriteria = {
  category: SeriesCriteriaCategory;
  value: string; // ontology_id
  displayName: string; // display_name for UI
};

export type HangingProtocolFormState = {
  hangingProtocolName: string;
  studyCriteriaSourceText: string;
  studyClassification: Array<ExtendedStudyCriteria>;
  ontologyVersion: string;
  submitFormIntent: SubmitFormIntent;
  /**
   * Discriminator identifying which HP this form state belongs to: the HP smid
   * in UPDATE mode, or null in CREATE mode. Used by the cross-window merge fn to
   * tell a same-HP broadcast race (preserve cross-window viewports) from a
   * case/HP switch (drop stale viewports). Optional so legacy writers that don't
   * set it behave exactly as before.
   */
  hpSmid?: Nullable<string>;
  /**
   * Set only on the value a window broadcasts when its form actually closes.
   *
   * Exists because a genuine close and "this window simply has no form open" are byte-identical
   * otherwise -- both are `defaultHPFormState` -- and the two need opposite handling: a close must
   * clear every window, while a bare default from a newly connected window must not touch a form
   * the user is editing. Optional, so a writer that never sets it keeps the old meaning of "no form
   * content", which is what the receiver assumes when it is absent.
   */
  closeSignal?: boolean;
  viewports: {
    [viewportId: string]: {
      seriesCriteriaSourceText: string;
      comparisonIndex: number | null;
      seriesClassificationCriteria?: Array<ExtendedSeriesCriteria>; // Optional - omitted during init, re-fetched from server
      isClearViewport: boolean;
      viewType: ViewType;
    };
  };
};

/**
 * The value a window broadcasts when its form closes. Distinct from `defaultHPFormState` only by
 * `closeSignal`, which is what lets the other windows tell a real close from an idle window's
 * default state.
 */
export const hpFormCloseState: HangingProtocolFormState = {
  hangingProtocolName: '',
  studyCriteriaSourceText: '',
  studyClassification: [],
  ontologyVersion: '',
  submitFormIntent: SUBMIT_FORM_INTENT.SAVE,
  hpSmid: null,
  closeSignal: true,
  viewports: {},
};

export const defaultHPFormState: HangingProtocolFormState = {
  hangingProtocolName: '',
  studyCriteriaSourceText: '',
  studyClassification: [],
  ontologyVersion: '',
  submitFormIntent: SUBMIT_FORM_INTENT.SAVE,
  hpSmid: null,
  viewports: {},
};

/**
 * Eagerly applies a mode + HP identity change to an existing form state, for callers that write
 * `submitFormIntent`/`hpSmid` ahead of the form's own full re-init (e.g. HangingProtocolWidget's
 * `open()` and its URL-param effect, and HangingProtocolsFormV4's own eager-set effect).
 *
 * Both fields are set together intentionally: the merge fn's HP-switch discriminator
 * (`hangingProtocolFormStateMergeFn`) compares `hpSmid` to tell a same-HP broadcast race from a
 * real HP switch, so `submitFormIntent` flipping to UPDATE must always carry the matching `hpSmid`
 * in the same write -- never a stale one left over from `prev`.
 *
 * `closeSignal` is always cleared to `false`. `hpFormCloseState` (broadcast on form close) sets it
 * `true` and nothing else ever resets it, since it is meant to be a one-shot flag consumed by the
 * merge fn. Any eager write that spreads `prev` without clearing it would carry a stale `true`
 * forward, and the merge fn treats `closeSignal: true` as an authoritative close -- silently
 * resetting every window back to the default "creating" state regardless of the mode just set
 * here (VX-3589).
 */
export const withFormIntent = (
  prev: HangingProtocolFormState,
  mode: SubmitFormIntent,
  hpSmid: Nullable<string>
): HangingProtocolFormState => ({
  ...prev,
  submitFormIntent: mode,
  hpSmid: mode === SUBMIT_FORM_INTENT.UPDATE ? (hpSmid ?? null) : null,
  closeSignal: false,
});

/**
 * True when a value is indistinguishable from `defaultHPFormState`, i.e. it carries no form content
 * at all. Checked field by field rather than by deep-equality against the constant so a field added
 * to the type later cannot silently widen what counts as "empty".
 */
const isDefaultFormBroadcast = (value: HangingProtocolFormState): boolean =>
  value.submitFormIntent === SUBMIT_FORM_INTENT.SAVE &&
  Object.keys(value.viewports).length === 0 &&
  (value.hpSmid ?? null) == null &&
  value.hangingProtocolName === '' &&
  value.studyCriteriaSourceText === '' &&
  value.studyClassification.length === 0;

/** True when this window is showing a form the user could be editing. */
const isFormOpenLocally = (value: HangingProtocolFormState): boolean =>
  value.submitFormIntent === SUBMIT_FORM_INTENT.UPDATE ||
  Object.keys(value.viewports).length > 0 ||
  (value.hpSmid ?? null) != null;

/**
 * Merge function for cross-window state synchronization.
 * This handles a race condition where a secondary window (e.g., viewer/1) may broadcast
 * its initial default state before receiving the synchronized state from the primary window.
 *
 * The merge strategy:
 * - For study-level fields: prefer non-empty values (the window with the form has the real data)
 * - For viewports: merge all viewports from both windows (each window manages its own viewports)
 */
export const hangingProtocolFormStateMergeFn = (
  oldValue: Nullable<HangingProtocolFormState>,
  newValue: HangingProtocolFormState
): HangingProtocolFormState => {
  if (!oldValue) return newValue;

  // A real form close, marked as such by the window that closed it. Accepted wholesale so stale
  // viewport entries are cleared everywhere and no window is left locked in UPDATE. `closeSignal`
  // is dropped on the way in so the flag does not linger as state.
  if (newValue.closeSignal === true) {
    return { ...defaultHPFormState };
  }

  // An unmarked default is not a close -- it is a window that simply has no form open, e.g. a second
  // viewer that just connected. It must not wipe a form the user is editing here. Shape alone cannot
  // separate the two cases, which is why the close is marked; before it was, any such broadcast blanked
  // the open form. The reported symptom was the name field coming up empty and submit then failing
  // validation on an HP that plainly has a name, cured by closing and reopening because that re-runs
  // the form's own initialization.
  if (isDefaultFormBroadcast(newValue) && isFormOpenLocally(oldValue)) {
    return oldValue;
  }

  // HP-switch discriminator: when the form moves to a different HP (or between
  // create and editing a specific HP), the old state is stale and must not bleed
  // across via the cross-window-preserve rule below. Take the incoming broadcast
  // wholesale. Legacy writers that never set hpSmid leave it null on both sides,
  // so this is a no-op for them (behavior unchanged).
  if ((oldValue.hpSmid ?? null) !== (newValue.hpSmid ?? null)) {
    return newValue;
  }

  // Same shape as above but with no form open locally: nothing to protect, so take it. Keeps a
  // window that never had a form from accumulating another window's stale viewport entries.
  if (isDefaultFormBroadcast(newValue)) {
    return newValue;
  }

  // Viewport IDs follow the pattern Viewport-{windowId}-{row}-{col}.
  // Extract which window IDs the incoming broadcast covers so we can:
  //  1. Accept all viewports from the broadcast (merging criteria when needed)
  //  2. Preserve old viewports from DIFFERENT windows (cross-window state)
  //  3. Drop stale old viewports from the SAME windows (prevents accumulation)
  //
  // Layout shrink is handled here automatically: when a window broadcasts
  // with fewer viewports, newWindowIds still contains that window's ID, so
  // old viewport entries from that window that were not re-broadcast are
  // dropped in the second loop below.
  const newWindowIds = new Set<string>();
  for (const id of Object.keys(newValue.viewports)) {
    newWindowIds.add(viewportIdToConfig(id).windowId);
  }

  const mergedViewports: HangingProtocolFormState['viewports'] = {};

  // Include all viewports from the incoming broadcast, preserving local criteria
  // when the broadcast clears them (stale echo or async classifier race).
  for (const [id, newVp] of Object.entries(newValue.viewports)) {
    const oldVp = oldValue.viewports[id];
    const oldHasCriteria = (oldVp?.seriesClassificationCriteria?.length ?? 0) > 0;
    const newHasCriteria = (newVp.seriesClassificationCriteria?.length ?? 0) > 0;
    // A broadcast naming a *different* series is a real change, not an echo. Missing criteria alone
    // cannot tell the two apart: `stepViewportToFormViewport` omits the key entirely for a step
    // viewport with no criteria, and the classifier populates it asynchronously, so a genuine step
    // switch routinely broadcasts the new step's source text with no criteria yet attached. Treating
    // that as an echo kept the previous step's source text (the old `||` preferred it whenever it was
    // non-empty) and pinned the other window's panel to the step the user had just left -- permanently,
    // since nothing re-broadcasts it. An empty incoming source text is still an echo and still loses.
    const newSourceText = newVp.seriesCriteriaSourceText ?? '';
    const oldSourceText = oldVp?.seriesCriteriaSourceText ?? '';
    const namesADifferentSeries = newSourceText !== '' && newSourceText !== oldSourceText;
    if (oldVp != null && oldHasCriteria && !newHasCriteria && !namesADifferentSeries) {
      // Stale echo: the broadcast lost criteria (e.g., child viewport init
      // effects fire before the parent's finishInit populates step data).
      // Preserve both classification criteria AND source text from local state
      // so step-loaded values like "R CC" aren't overwritten by empty strings.
      mergedViewports[id] = {
        ...newVp,
        seriesClassificationCriteria: oldVp.seriesClassificationCriteria,
        seriesCriteriaSourceText: oldSourceText || newSourceText,
        // The user's declared prior scope, kept for the same reason as the two fields above: on an echo
        // the local copy is the authoritative one. `comparisonIndex` is not derived and nothing
        // recomputes it, so taking the broadcast's older value silently un-declares the choice the
        // radiologist made in the study dropdown. It also cannot be noticed afterwards: the form then
        // genuinely matches its baseline, so Update closes without sending a mutation and the change is
        // lost with no error. Only the window owning a viewport originates its scope; every other
        // window echoes, which is what makes preferring local correct here.
        comparisonIndex: oldVp.comparisonIndex,
      };
    } else {
      // Wholesale, criteria included: when the series changes, the old classification described the
      // series being replaced, so carrying it over would show the previous step's criteria under the
      // new series' name.
      mergedViewports[id] = newVp;
    }
  }

  // Preserve old viewports from windows NOT represented in the broadcast.
  // This keeps cross-window state intact during the race where one window
  // broadcasts before receiving the other window's viewports.
  for (const [id, oldVp] of Object.entries(oldValue.viewports)) {
    if (id in mergedViewports) continue;
    const { windowId } = viewportIdToConfig(id);
    if (!newWindowIds.has(windowId)) {
      mergedViewports[id] = oldVp;
    }
  }

  return {
    // Prefer non-empty study-level fields
    hangingProtocolName: newValue.hangingProtocolName || oldValue.hangingProtocolName,
    studyCriteriaSourceText: newValue.studyCriteriaSourceText || oldValue.studyCriteriaSourceText,
    studyClassification:
      newValue.studyClassification.length > 0
        ? newValue.studyClassification
        : oldValue.studyClassification,
    ontologyVersion: newValue.ontologyVersion || oldValue.ontologyVersion,
    // Protect UPDATE intent from stale broadcast echoes: when the form is open
    // (UPDATE + viewports populated), a delayed echo with SAVE must not overwrite it.
    // But when the form closes (broadcasts defaultHPFormState with SAVE + empty viewports),
    // all windows must accept the SAVE so UPDATE doesn't get stuck permanently.
    submitFormIntent:
      oldValue.submitFormIntent === SUBMIT_FORM_INTENT.UPDATE &&
      Object.keys(newValue.viewports).length > 0
        ? SUBMIT_FORM_INTENT.UPDATE
        : newValue.submitFormIntent,
    // Same HP on this path (the switch case returned early), so either side's
    // hpSmid is correct; carry it through.
    hpSmid: newValue.hpSmid,
    viewports: mergedViewports,
  };
};

/**
 * Builds the form's viewport entries from a step: the viewports the step defines, plus a cleared
 * placeholder for every layout slot it does not.
 *
 * `restrictToWindowId` decides which windows this call is allowed to speak for.
 *
 * In UPDATE the stored protocol is authoritative for every window, so the caller leaves it
 * undefined -- including for windows the user does not currently have open, whose configuration
 * must survive the round trip rather than be dropped from the next save.
 *
 * In CREATE it is not authoritative for anything. There is no stored protocol yet; each window
 * seeds its own viewports from what it currently has hung, in `HangingProtocolViewport`'s init
 * effect. `hangingProtocolFormState` is BroadcastChannel-synced, and `hangingProtocolFormStateMergeFn`
 * treats a broadcast that covers a window as authoritative for that window. So emitting cleared
 * placeholders for the other window overwrites the series the radiologist hung there -- and nothing
 * puts them back, because that init effect runs once per mount and refuses to write for a window
 * other than its own. Worse, the ordering is reliably against us: the sidebar-state broadcast that
 * opens the panels on viewer/1 arrives before this state does, so viewer/1 has always written its
 * viewports by the time the placeholders land on top of them.
 *
 * Symptom when this is unscoped: hang series on viewer/1, click Create HP on viewer/0, and every
 * viewer/1 panel reads "Clear Viewport".
 */
export function buildStepFormViewports({
  step,
  layouts,
  restrictToWindowId,
}: {
  step: StepConfiguration;
  layouts: Layouts;
  restrictToWindowId?: string;
}): HangingProtocolFormState['viewports'] {
  const owns = (windowId: string): boolean =>
    restrictToWindowId == null || windowId === restrictToWindowId;

  const viewports: HangingProtocolFormState['viewports'] = {};

  for (const [viewportId, entry] of Object.entries(step.viewports)) {
    if (!owns(viewportIdToConfig(viewportId).windowId)) continue;
    viewports[viewportId] = stepViewportToFormViewport(entry);
  }

  // Fill missing layout positions as cleared. Check both the step's layouts AND the current viewer
  // layouts so that windows not defined in the step (e.g. window 1 in dual-monitor mode) still get
  // their viewports marked as cleared.
  const allLayouts: Record<string, [number, number]> = {};
  for (const [windowId, layoutStr] of Object.entries(step.layouts)) {
    allLayouts[windowId] = parseLayoutString(layoutStr);
  }
  for (const [windowId, layout] of Object.entries(layouts)) {
    allLayouts[windowId] ??= layout;
  }

  for (const [windowId, [rows, cols]] of Object.entries(allLayouts)) {
    if (!owns(windowId)) continue;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const vpId = generateViewportId(windowId, row, col);
        viewports[vpId] ??= {
          seriesCriteriaSourceText: '',
          comparisonIndex: null,
          isClearViewport: true,
          viewType: ViewType.TwoDDre,
        };
      }
    }
  }

  return viewports;
}

/**
 * Flattens a classification object (keyed by snake_case category) into an array of
 * { category, value, displayName } using the provided category enum map.
 */
function flattenClassification<C extends string>(
  classification: unknown,
  categoryMap: Record<string, C>
): Array<{ category: C; value: string; displayName: string }> {
  const result: Array<{ category: C; value: string; displayName: string }> = [];
  if (classification == null || typeof classification !== 'object') return result;

  for (const [key, values] of Object.entries(classification)) {
    const category = categoryMap[key];
    if (category && Array.isArray(values)) {
      values.forEach((item) => {
        if (item?.ontology_id) {
          result.push({
            category,
            value: item.ontology_id,
            displayName: item.display_name,
          });
        }
      });
    }
  }
  return result;
}

const STUDY_CATEGORY_MAP: Record<string, StudyCriteriaCategory> = {
  anatomic_focus: StudyCriteriaCategory.AnatomicFocus,
  contrast_status: StudyCriteriaCategory.ContrastStatus,
  field_of_view: StudyCriteriaCategory.FieldOfView,
  modality: StudyCriteriaCategory.Modality,
  reason_for_study: StudyCriteriaCategory.ReasonForStudy,
  view_count: StudyCriteriaCategory.ViewCount,
};

const SERIES_CATEGORY_MAP: Record<string, SeriesCriteriaCategory> = {
  anatomic_focus: SeriesCriteriaCategory.AnatomicFocus,
  contrast_status: SeriesCriteriaCategory.ContrastStatus,
  ct_kernel: SeriesCriteriaCategory.CtKernel,
  dimensionality: SeriesCriteriaCategory.Dimensionality,
  document_type: SeriesCriteriaCategory.DocumentType,
  field_of_view: SeriesCriteriaCategory.FieldOfView,
  localizer: SeriesCriteriaCategory.Localizer,
  modality: SeriesCriteriaCategory.Modality,
  mr_relative_phase: SeriesCriteriaCategory.MrRelativePhase,
  mr_weighting: SeriesCriteriaCategory.MrWeighting,
  patient_position: SeriesCriteriaCategory.PatientPosition,
  post_processing: SeriesCriteriaCategory.PostProcessing,
  tissue_selection: SeriesCriteriaCategory.TissueSelection,
  view_orientation: SeriesCriteriaCategory.ViewOrientation,
};

/**
 * Merge a live step's viewports (from `steppedHPConfigurations`) with the same step's
 * raw-fetched viewports (from a direct backend fetch) for UPDATE-mode form init.
 *
 * The raw fetch has complete, unfiltered viewport definitions — including criteria-based
 * viewports the match query stripped because the current case has no matching series. The live
 * atom may instead hold a clinical edit (e.g. a drag-and-drop series assignment) that hasn't
 * been persisted to the backend yet, which the raw fetch can't know about.
 *
 * Per viewport, prefer the live entry when it's non-empty (`isEmptyViewport` — has a display
 * name, criteria, or series criteria text), since that only happens via a resolved criteria match
 * or a clinical edit, both more current than the backend copy. Otherwise fall back to the fetched
 * entry so an unmatched criteria-based viewport definition stays visible/editable instead of
 * appearing as a cleared, empty viewport (VX-4614).
 */
export function mergeStepViewportsForUpdate(
  liveViewports: Record<string, ViewportConfigurationEntry>,
  fetchedViewports: Record<string, ViewportConfigurationEntry>
): Record<string, ViewportConfigurationEntry> {
  const viewportIds = new Set([...Object.keys(liveViewports), ...Object.keys(fetchedViewports)]);
  const merged: Record<string, ViewportConfigurationEntry> = {};
  for (const id of viewportIds) {
    const live = liveViewports[id];
    const fetched = fetchedViewports[id];
    merged[id] = live != null && !isEmptyViewport(live) ? live : (fetched ?? live)!;
  }
  return merged;
}

export function flattenStudyClassification(classification: unknown): Array<ExtendedStudyCriteria> {
  return flattenClassification(classification, STUDY_CATEGORY_MAP);
}

export function flattenSeriesClassification(
  classification: unknown
): Array<ExtendedSeriesCriteria> {
  return flattenClassification(classification, SERIES_CATEGORY_MAP);
}

/**
 * True when the steps the form would send are identical to the ones it started from, i.e. this save
 * changes nothing about the HP's steps.
 *
 * Compares the GQL inputs rather than the `StepConfiguration` objects, so the answer is about what
 * would actually go over the wire: two step lists that serialize identically produce the same
 * backend write regardless of how their in-memory shapes differ. That also makes the check safe by
 * construction -- a difference the comparison invents (the form's step sync rewrites viewport
 * formats and accumulates layout changes) only costs the caller the chance to omit `steps`, while
 * missing a real difference would drop the user's edit. It errs toward "changed", never toward
 * silently discarding step content. Note this is deliberately stricter than `hasUnsavedChanges`,
 * which compares only a step's name, smid and config_details.
 */
export const areStepsUnchanged = (
  currentSteps: StepConfiguration[] | null,
  initialSteps: StepConfiguration[] | null
): boolean => {
  if (currentSteps == null || initialSteps == null) return false;
  if (currentSteps.length !== initialSteps.length) return false;
  return equals(currentSteps.map(toComparableStepInput), initialSteps.map(toComparableStepInput));
};

/**
 * Normalizes a step's GQL input into a form whose deep equality reflects content alone.
 *
 * `stepToGqlInput` builds its `layouts` and `viewports` arrays with `Object.entries`, so their order
 * follows key insertion order rather than anything meaningful. Form step sync and BroadcastChannel
 * round trips both produce different insertion orders for identical data, which a plain deep compare
 * reads as a change -- the same hazard `hasUnsavedChanges` defends against by sorting its viewport
 * entries. Left unsorted it would silently resend `steps` on a name-only save and reintroduce the
 * smid churn this is meant to avoid. Sorting happens here rather than in `stepToGqlInput` so the
 * payload actually sent to the backend is untouched.
 *
 * `sub_steps` keeps its array order, which is the sub-step sequence and therefore meaningful; only
 * the viewports within each are sorted.
 */
const toComparableStepInput = (step: StepConfiguration) => {
  const input = stepToGqlInput(step);
  const sortByViewportId = <T extends { viewport_id: string }>(viewports: readonly T[]) =>
    [...viewports].sort((a, b) => a.viewport_id.localeCompare(b.viewport_id));
  const sortByWindowId = <T extends { window_id: string }>(layouts: readonly T[]) =>
    [...layouts].sort((a, b) => a.window_id.localeCompare(b.window_id));

  return {
    ...input,
    layouts: sortByWindowId(input.layouts ?? []),
    viewports: sortByViewportId(input.viewports ?? []),
    sub_steps:
      input.sub_steps?.map((subStep) => ({
        ...subStep,
        viewports: sortByViewportId(subStep.viewports ?? []),
      })) ?? input.sub_steps,
  };
};

export const generatePayload = ({
  formState,
  layouts,
  numberOfPriors,
  steps,
  studyDescriptions,
  groupedStudies,
  preserveExistingSteps = false,
}: {
  formState: HangingProtocolFormState;
  layouts: Layouts;
  numberOfPriors: number;
  steps?: StepConfiguration[] | null;
  studyDescriptions?: string[];
  groupedStudies?: string[];
  /**
   * Send `preserveExistingSteps` instead of the `steps` array, leaving the backend's existing step
   * rows untouched. Only valid on an update whose step content is unchanged (VX-4766).
   */
  preserveExistingSteps?: boolean;
}) => {
  // When steps are present, the backend reads per-step layouts from step.layouts,
  // not from data.layouts. The frontend still sends data.layouts for non-stepped HPs
  // and as a fallback for the HP's "base" layout (used in the HP list/preview).
  // Each step carries its own layouts (step.layouts). The backend ignores data.layouts when steps are
  // present, so this value only matters for non-stepped HPs.
  const layoutsPayload = Object.entries(layouts).map(([id, layout]) => ({
    id,
    layout,
  }));

  // Filter out viewportIds that are not visible in the layouts or are marked as cleared
  const visibleViewports = Object.entries(formState.viewports).filter(
    ([viewportId, viewportConfig]) => {
      // Filter out cleared viewports
      if (viewportConfig.isClearViewport) return false;

      try {
        const { windowId, rowIndex, colIndex } = viewportIdToConfig(viewportId);
        const layout = layoutsPayload.find((layout) => layout.id === windowId)?.layout;
        if (!layout) return false;
        return layout[0] > rowIndex && layout[1] > colIndex;
      } catch (err) {
        logger.warn('[generatePayload] Skipping viewport with invalid ID', { viewportId, err });
        return false;
      }
    }
  );

  const viewportsConfigurationsPayload = visibleViewports.map(([viewportId, viewportConfig]) => ({
    id: viewportId,
    comparisonIndex: viewportConfig.comparisonIndex,
    seriesCriteriaSourceText: viewportConfig.seriesCriteriaSourceText,
    seriesDescriptions: [],
    viewType: viewportConfig.viewType,
    criteria: (viewportConfig.seriesClassificationCriteria ?? []).map((criteria) => ({
      category: criteria.category,
      value: criteria.value,
    })),
  }));

  const payload = {
    name: formState.hangingProtocolName,
    // Send undefined instead of empty string - backend rejects blank but accepts omitted
    ontologyVersion: formState.ontologyVersion || undefined,
    studyCriteriaSourceText: formState.studyCriteriaSourceText,
    studyDescriptions: studyDescriptions ?? [],
    numberOfPriors: numberOfPriors,
    groupedStudies: groupedStudies ?? [],
    studyCriteria: formState.studyClassification.map((criteria) => ({
      category: criteria.category,
      value: criteria.value,
    })),
    data: {
      layouts: layoutsPayload,
      viewportsConfigurations: viewportsConfigurationsPayload,
    },
    ...(steps != null && steps.length > 0
      ? preserveExistingSteps
        ? // Omitting `steps` is what keeps the backend from deleting and recreating every step row;
          // the flag tells the resolver this is a stepped HP so it doesn't fall back to sending
          // layouts/viewports, which the backend rejects for an HP that has steps.
          { preserveExistingSteps: true }
        : { steps: steps.map((step) => stepToGqlInput(step)) }
      : {}),
  };

  return payload as HangingProtocolPayload;
};

/**
 * Which of the two situations a dirty check is being asked about, with respect to step layouts.
 *
 * A caller must state one. There is no default and no "unset", because the whole point of this enum
 * is that skipping the step-layout comparison should be a claim the caller makes, not a field it
 * omitted. See {@link hasUnsavedChanges} for the defect history behind that.
 */
export enum StepLayoutContextKind {
  /**
   * There is no HP-owned layout to compare against at all -- e.g. CREATE, where there is no
   * existing HP whose stored layout could differ from the current viewer layout.
   *
   * `hasUnsavedChanges` then answers purely from the form-state and step-configuration
   * comparisons. Choosing this for an UPDATE (stepped or flat) reintroduces VX-4771/VX-4938: the
   * layout picker writes to the viewer's layout atoms, invisible to every other comparison in
   * `hasUnsavedChanges`, so a layout-only edit reads as clean and Update becomes a silent no-op.
   */
  None = 'none',

  /**
   * A step is active, so its stored layout has to be compared against the layout a save would write.
   *
   * Requires `activeStepLayouts` (what the step has now) and `currentLayouts` (what a save would put
   * there). A difference means unsaved changes, and nothing else in `hasUnsavedChanges` can see it:
   * the layout picker writes to the viewer's layout atoms, and the layout only becomes part of the
   * step inside `flushCurrentStepToConfigurations()`, which runs after the check.
   *
   * Read both from the same source that flush reads, or the check and the save can disagree about
   * what is about to be written.
   */
  ActiveStep = 'activeStep',

  /**
   * Editing a flat (non-stepped) HP, so the HP's own stored `data.layouts` has to be compared
   * against the layout a save would write.
   *
   * Same blind spot as `ActiveStep`, just for the non-stepped shape: the layout picker writes to
   * the viewer's layout atoms, which `generatePayload` always sends as `data.layouts` regardless
   * of whether anything else on the form changed. Without this, a layout-only edit on a flat HP is
   * invisible to `hasUnsavedChanges` and Update silently no-ops (VX-4938).
   */
  FlatHp = 'flatHp',
}

/**
 * Whether an HP-owned layout is in play for a dirty check, and the data to compare if it is.
 *
 * See {@link hasUnsavedChanges} for why this is a union a caller must fill in rather than a pair of
 * optional fields.
 */
export type StepLayoutContext =
  | { kind: StepLayoutContextKind.None }
  | {
      kind: StepLayoutContextKind.ActiveStep;
      /** The active step's stored layouts, `windowId -> "rows,cols"`. */
      activeStepLayouts: Record<string, string>;
      /** The layouts a save would write, from the same source `flushCurrentStepToConfigurations` reads. */
      currentLayouts: Nullable<Layouts>;
    }
  | {
      kind: StepLayoutContextKind.FlatHp;
      /** The flat HP's stored layouts, `windowId -> "rows,cols"`, from `hangingProtocol.data.layouts`. */
      storedLayouts: Record<string, string>;
      /** The layouts a save would write -- the same viewer layouts `generatePayload` reads. */
      currentLayouts: Nullable<Layouts>;
    };

/** Convenience for the common "no active step" case, so callers read as intent. */
export const NO_STEP_LAYOUT_CONTEXT: StepLayoutContext = { kind: StepLayoutContextKind.None };

/**
 * Deep comparison helper to detect if there are unsaved changes.
 * Compares form state and step configurations against their initial snapshots.
 */
export const hasUnsavedChanges = ({
  currentFormState,
  initialFormState,
  currentStepConfigurations,
  initialStepConfigurations,
  stepContext,
}: {
  currentFormState: HangingProtocolFormState;
  initialFormState: HangingProtocolFormState | null;
  currentStepConfigurations: StepConfiguration[] | null;
  initialStepConfigurations: StepConfiguration[] | null;
  /**
   * Whether there is an HP-owned layout (an active step's, or a flat HP's own) that could have
   * changed, and if so its stored layouts plus the layouts a save would write.
   *
   * Required, and a discriminated union rather than two optional fields, on purpose. A layout change
   * is invisible to every other comparison in here: the picker writes to the viewer's layout atoms,
   * and that only becomes part of the payload inside `flushCurrentStepToConfigurations()` /
   * `generatePayload()`, which runs *after* this check. Omitting it made an Update that changed only
   * a grid take the no-op early return -- first for stepped HPs (VX-4771), then again for flat HPs
   * (VX-4938) -- and made Cancel skip the discard prompt, so the same edit was discarded in silence on
   * both paths.
   *
   * Optional fields would let a future caller inherit that blind spot by saying nothing, which is how
   * the sibling layout defect became five tickets instead of one (VX-4624, VX-4732, VX-4768,
   * VX-4772, VX-4938): each fix reached only the call site its ticket travelled through. Here a caller
   * with no HP-owned layout -- CREATE, where there is nothing stored yet to diff against -- has to say
   * `{ kind: 'none' }`, so skipping the layout comparison is a decision on the record instead of a
   * field someone forgot.
   */
  stepContext: StepLayoutContext;
}): boolean => {
  // If no initial state was captured, assume no changes
  if (!initialFormState) return false;

  if (
    stepContext.kind === StepLayoutContextKind.ActiveStep &&
    stepLayoutWouldChangeOnSave({
      activeStepLayouts: stepContext.activeStepLayouts,
      currentLayouts: stepContext.currentLayouts,
    })
  ) {
    return true;
  }

  if (
    stepContext.kind === StepLayoutContextKind.FlatHp &&
    stepLayoutWouldChangeOnSave({
      activeStepLayouts: stepContext.storedLayouts,
      currentLayouts: stepContext.currentLayouts,
    })
  ) {
    return true;
  }

  // Compare form state fields (excluding submitFormIntent which is mode-specific,
  // and studyClassification/ontologyVersion which are auto-populated by the
  // classifier after init — not user-driven changes).
  if (currentFormState.hangingProtocolName !== initialFormState.hangingProtocolName) return true;
  if (currentFormState.studyCriteriaSourceText !== initialFormState.studyCriteriaSourceText)
    return true;

  // Compare user-editable viewport fields only. Exclude seriesClassificationCriteria
  // which is auto-populated by the classifier (with resolved displayNames) after init.
  const pickEditableFields = (vp: HangingProtocolFormState['viewports'][string]) => ({
    seriesCriteriaSourceText: vp.seriesCriteriaSourceText,
    comparisonIndex: vp.comparisonIndex,
    viewType: vp.viewType,
    isClearViewport: vp.isClearViewport,
  });
  // Sort entries by viewport ID so key insertion order doesn't cause false diffs.
  // BroadcastChannel sync and form init can produce different key orderings for
  // identical viewport data.
  const currentVps = Object.fromEntries(
    Object.entries(currentFormState.viewports)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, vp]) => [id, pickEditableFields(vp)])
  );
  const initialVps = Object.fromEntries(
    Object.entries(initialFormState.viewports)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, vp]) => [id, pickEditableFields(vp)])
  );
  if (!equals(currentVps, initialVps)) {
    return true;
  }

  // Compare step configurations using only user-meaningful fields.
  // The form's step sync adds cleared viewport entries, modifies viewport formats,
  // and accumulates layout changes that cause false diffs with naive comparison.
  if (currentStepConfigurations != null && initialStepConfigurations != null) {
    if (currentStepConfigurations.length !== initialStepConfigurations.length) return true;
    for (let i = 0; i < currentStepConfigurations.length; i++) {
      const curr = currentStepConfigurations[i];
      const init = initialStepConfigurations[i];
      if (curr.display_name !== init.display_name) return true;
      if (curr.smid !== init.smid) return true;
      if (!equals(curr.config_details, init.config_details)) return true;
    }
  } else if (currentStepConfigurations !== initialStepConfigurations) {
    return true;
  }

  return false;
};

/**
 * Whether saving now would change the active step's stored layouts.
 *
 * A layout change is invisible to {@link hasUnsavedChanges}: the layout picker writes to the
 * viewer's layout atoms, and the layout only becomes part of a step inside
 * `flushCurrentStepToConfigurations()`, which runs after the no-op check. So an Update that changed
 * only a step's grid took the early return, fired no mutation, and then `restoreInitialLayouts()`
 * put the old grid back -- the change was discarded with no error and no toast (VX-4771).
 *
 * This mirrors what the flush actually writes, so the two cannot disagree. The flush sets every
 * window present in the viewer's current layouts and preserves step-owned windows the viewer no
 * longer reports, so the stored record changes exactly when some current window's layout differs
 * from what the step has -- including a window the step does not define yet, which the flush adopts.
 *
 * Iterating the *current* windows rather than the step's own is the correction to the first VX-4771
 * fix, which compared only windows the step already owned. On a dual-window viewer whose step had
 * never stored window "1", a layout change there read as clean and was silently discarded -- the
 * configuration QA re-reported ("different layouts in Viewer 1 and Viewer 2").
 *
 * Trade-off: the first Update on a dual-window viewer now reports a change even if the user edited
 * nothing, because the flush would genuinely add that window to the step. That fires one extra
 * mutation and clears clinical dirty state in a case where it previously would not. Silently
 * dropping a layout the user explicitly picked is the worse failure, and the condition self-heals
 * once the step owns both windows.
 */
export function stepLayoutWouldChangeOnSave({
  activeStepLayouts,
  currentLayouts,
}: {
  activeStepLayouts: Record<string, string>;
  currentLayouts: Nullable<Layouts>;
}): boolean {
  if (currentLayouts == null) return false;
  return Object.entries(currentLayouts).some(
    ([windowId, layout]) => layout.join(',') !== activeStepLayouts[windowId]
  );
}
